import {
  Box,
  Button,
  Dialog,
  FormControlLabel,
  Switch,
  DialogActions,
  DialogContent,
  DialogTitle,
  MenuItem,
  Paper,
  TextField,
  Typography,
} from '@mui/material';
import type { FormEvent } from 'react';
import { useState } from 'react';
import type { CustomerDto, NoteDto } from '@app/shared';
import { ConfirmDialog, FormDialog } from '../../components/dialogs';
import { useNotify } from '../../components/NotifyProvider';
import { formatDateTime } from '../../lib/format';
import { Can } from '../auth/guards';
import {
  useAddRetentionNote,
  useCancelAutoPay,
  useCustomerNotes,
  useSendBalanceSms,
  useSetNotePriority,
  useSetPortoutLock,
} from './api';
import { QrCode } from './MoreActionForms';
import { Field, FieldGrid, userLabel } from './parts';

/** The levels the reference's "Highest Priority Notes" dialog offers: those below Highest. */
const LOWER_PRIORITIES = ['High', 'Medium', 'Low', 'Lowest'] as const;

function PriorityRow({ customerId, note }: { customerId: string; note: NoteDto }) {
  const notify = useNotify();
  const [priority, setPriority] = useState<(typeof LOWER_PRIORITIES)[number]>('High');
  const update = useSetNotePriority(customerId);
  const save = async () => {
    try {
      await update.mutateAsync({ noteId: note.id, priority });
      notify.success('Note priority updated.');
    } catch (err) {
      notify.error(err);
    }
  };
  return (
    <Paper variant="outlined" sx={{ p: 1.25, mb: 1 }}>
      <Typography sx={{ mb: 0.5 }}>{note.text}</Typography>
      <Typography variant="caption" color="text.secondary" component="div" sx={{ mb: 1 }}>
        {note.noteType} · {userLabel(note.createdBy)} · {formatDateTime(note.createdAt)}
      </Typography>
      <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
        <TextField
          select
          size="small"
          label="Priority"
          value={priority}
          onChange={(e) => setPriority(e.target.value as (typeof LOWER_PRIORITIES)[number])}
          sx={{ minWidth: 160 }}
        >
          {LOWER_PRIORITIES.map((p) => (
            <MenuItem key={p} value={p}>
              {p}
            </MenuItem>
          ))}
        </TextField>
        <Button variant="contained" disabled={update.isPending} onClick={() => void save()}>
          Update
        </Button>
      </Box>
    </Paper>
  );
}

/**
 * "Highest Priority Notes": shown when the customer has notes of the Highest priority. The
 * dialog lists them, each with the reference's priority select {High, Medium, Low, Lowest} and
 * Update. Assumption: where the reference opens it from was not seen; here it is a button by
 * the notes, and it asks for Add Notes (PC83).
 */
export function HighestPriorityNotes({ customerId }: { customerId: string }) {
  const [open, setOpen] = useState(false);
  const notes = useCustomerNotes(customerId, { priority: 'Highest', page: 1, pageSize: 50 });
  const rows = notes.data?.data ?? [];
  if (!rows.length && !open) return null;
  return (
    <Can code="PC83">
      <Button variant="outlined" color="warning" onClick={() => setOpen(true)}>
        Highest Priority Notes ({notes.data?.total ?? rows.length})
      </Button>
      <Dialog open={open} onClose={() => setOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Highest Priority Notes</DialogTitle>
        <DialogContent dividers>
          {rows.length ? (
            rows.map((n) => <PriorityRow key={n.id} customerId={customerId} note={n} />)
          ) : (
            <Typography color="text.secondary">No note has the Highest priority now.</Typography>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpen(false)}>Close</Button>
        </DialogActions>
      </Dialog>
    </Can>
  );
}

/**
 * "Cancel AutoPay" with the reference's "Confirm Cancellation" (Auto Pay, PC574). Shown while
 * Auto Pay is on. The confirmation text is ours.
 */
export function CancelAutoPay({ customer }: { customer: CustomerDto }) {
  const notify = useNotify();
  const [open, setOpen] = useState(false);
  const cancel = useCancelAutoPay(customer.id);
  if (!customer.other.autopay) return null;
  const confirm = async () => {
    try {
      await cancel.mutateAsync(undefined);
      notify.success('Auto Pay cancelled.');
      setOpen(false);
    } catch (err) {
      notify.error(err);
    }
  };
  return (
    <Can code="PC574">
      <Button size="small" color="error" sx={{ ml: 1 }} onClick={() => setOpen(true)}>
        Cancel AutoPay
      </Button>
      <ConfirmDialog
        open={open}
        title="Cancel AutoPay"
        message="Auto Pay will stop renewing this customer's plan from the wallet."
        confirmLabel="Confirm Cancellation"
        danger
        busy={cancel.isPending}
        onConfirm={() => void confirm()}
        onClose={() => setOpen(false)}
      />
    </Can>
  );
}

/**
 * "Current Shipping Info": the status of the customer's shipment order, its shipper and
 * tracking number. The reference's "Track Shipping" asks the shipper where the parcel is; the
 * ShipStation connection gives labels only, so that button is not offered.
 */
export function CurrentShippingInfo({ customer }: { customer: CustomerDto }) {
  const [open, setOpen] = useState(false);
  const shipping = customer.shipping;
  if (!shipping) return null;
  return (
    <>
      <Button variant="outlined" onClick={() => setOpen(true)}>
        Current Shipping Info
      </Button>
      <Dialog open={open} onClose={() => setOpen(false)} maxWidth="xs" fullWidth>
        <DialogTitle>Current Shipping Info</DialogTitle>
        <DialogContent dividers>
          <FieldGrid>
            <Field label="Shipping Status" value={shipping.status} />
            <Field label="Shipper" value={shipping.shipper} />
            <Field label="Tracking Number" value={shipping.trackingNumber} />
            <Field label="Shipment Date" value={formatDateTime(shipping.shippedAt)} />
          </FieldGrid>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpen(false)}>Close</Button>
        </DialogActions>
      </Dialog>
    </>
  );
}

/**
 * "Portout Lock" (Save): while on, a port-out request for the number is refused. The reference
 * dialog's fields were not seen; here it is one switch. Asks for Customer Information (PC377).
 */
export function PortoutLock({ customer }: { customer: CustomerDto }) {
  const notify = useNotify();
  const [open, setOpen] = useState(false);
  const [locked, setLocked] = useState(customer.portoutLock);
  const save = useSetPortoutLock(customer.id);
  if (!customer.line.mdn) return null;
  const submit = async (e: FormEvent) => {
    e.preventDefault();
    try {
      await save.mutateAsync(locked);
      notify.success(locked ? 'Port-out lock set.' : 'Port-out lock removed.');
      setOpen(false);
    } catch (err) {
      notify.error(err);
    }
  };
  return (
    <Can code="PC377">
      <Button
        variant="outlined"
        onClick={() => {
          setLocked(customer.portoutLock);
          setOpen(true);
        }}
      >
        Portout Lock{customer.portoutLock ? ' (on)' : ''}
      </Button>
      <FormDialog
        open={open}
        title="Portout Lock"
        onClose={() => setOpen(false)}
        onSubmit={(e) => void submit(e)}
        submitting={save.isPending}
        submitLabel="Save"
        maxWidth="xs"
      >
        <FormControlLabel
          label={`Lock ${customer.line.mdn} against port-out`}
          control={<Switch checked={locked} onChange={(e) => setLocked(e.target.checked)} />}
        />
        <Typography color="text.secondary" sx={{ fontSize: 13, mt: 1 }}>
          While locked, a request from another provider to take this number is refused.
        </Typography>
      </FormDialog>
    </Can>
  );
}

/** "Retention Note" (Save): a note listed under "Retention Note". Asks for Add Notes (PC83). */
export function RetentionNote({ customerId }: { customerId: string }) {
  const notify = useNotify();
  const [open, setOpen] = useState(false);
  const [text, setText] = useState('');
  const [problem, setProblem] = useState('');
  const add = useAddRetentionNote(customerId);
  const submit = async (e: FormEvent) => {
    e.preventDefault();
    if (!text.trim()) return setProblem('Enter the retention note');
    try {
      await add.mutateAsync(text.trim());
      notify.success('Retention note saved.');
      setOpen(false);
    } catch (err) {
      notify.error(err);
    }
  };
  return (
    <Can code="PC83">
      <Button
        variant="outlined"
        onClick={() => {
          setText('');
          setProblem('');
          setOpen(true);
        }}
      >
        Retention Note
      </Button>
      <FormDialog
        open={open}
        title="Retention Note"
        onClose={() => setOpen(false)}
        onSubmit={(e) => void submit(e)}
        submitting={add.isPending}
        submitLabel="Save"
      >
        <TextField
          label="Retention Note"
          fullWidth
          multiline
          minRows={4}
          value={text}
          onChange={(e) => {
            setText(e.target.value);
            setProblem('');
          }}
          error={Boolean(problem)}
          helperText={problem || `Characters remaining: ${1000 - text.length}`}
          slotProps={{ htmlInput: { maxLength: 1000 } }}
          sx={{ mt: 1 }}
        />
      </FormDialog>
    </Can>
  );
}

/** "Activation Code/QR-code": the eSIM profile of the last E-Sim Change, as a QR code and text. */
export function EsimActivationCode({ customer }: { customer: CustomerDto }) {
  const [open, setOpen] = useState(false);
  const code = customer.line.esimActivationCode;
  if (!code) return null;
  return (
    <>
      <Button variant="outlined" onClick={() => setOpen(true)}>
        Activation Code/QR-code
      </Button>
      <Dialog open={open} onClose={() => setOpen(false)} maxWidth="xs" fullWidth>
        <DialogTitle>Activation Code/QR-code</DialogTitle>
        <DialogContent dividers>
          <Box sx={{ display: 'flex', justifyContent: 'center', mb: 1 }}>
            <QrCode text={code} label="eSIM activation QR code" />
          </Box>
          <Typography sx={{ fontSize: 13, overflowWrap: 'anywhere' }}>{code}</Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpen(false)}>Close</Button>
        </DialogActions>
      </Dialog>
    </>
  );
}

/**
 * "Send SMS" with the reference's "Are you sure you want to send SMS?" (Yes / No): texts the
 * customer's wallet balance and plan expiry. Asks for Resend Notification (PC930).
 */
export function BalanceSms({ customer }: { customer: CustomerDto }) {
  const notify = useNotify();
  const [open, setOpen] = useState(false);
  const sendSms = useSendBalanceSms(customer.id);
  if (!customer.line.mdn && !customer.contactNumber) return null;
  const confirm = async () => {
    try {
      const res = await sendSms.mutateAsync(undefined);
      notify.success(res.message);
      setOpen(false);
    } catch (err) {
      notify.error(err);
    }
  };
  return (
    <Can code="PC930">
      <Button size="small" sx={{ ml: 1 }} onClick={() => setOpen(true)}>
        Send SMS
      </Button>
      <ConfirmDialog
        open={open}
        title="Send SMS"
        message="Are you sure you want to send SMS?"
        confirmLabel="Yes"
        busy={sendSms.isPending}
        onConfirm={() => void confirm()}
        onClose={() => setOpen(false)}
      />
    </Can>
  );
}
